// src/admin/pages/Requests.jsx
import React, { useEffect, useState, useCallback } from 'react';
import {
  Tabs, Tab, Box, Card, CardContent, Typography, Button, Stack, TextField, Snackbar, Alert, Chip,
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import PageHeader from '../components/PageHeader';
import ConfirmDialog from '../components/ConfirmDialog';
import { fetchSubmissions, approveRequest, rejectRequest } from '../lib/adminApi';
import { toDate } from '../lib/adminLogic';

export default function Requests() {
  const { currentUser } = useAuth();
  const [tab, setTab] = useState(0);
  const [pending, setPending] = useState([]);
  const [resolved, setResolved] = useState([]);
  const [rejectFor, setRejectFor] = useState(null);
  const [reason, setReason] = useState('');
  const [toast, setToast] = useState(null);

  const reload = useCallback(async () => {
    setPending(await fetchSubmissions(true));
    setResolved(await fetchSubmissions(false));
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const onApprove = async (req) => {
    await approveRequest(currentUser, req);
    setToast({ severity: 'success', msg: `${req.email} promosso a Pro` });
    await reload();
  };

  const onReject = async () => {
    await rejectRequest(currentUser, rejectFor, reason);
    setToast({ severity: 'info', msg: 'Richiesta rifiutata' });
    setRejectFor(null); setReason('');
    await reload();
  };

  return (
    <>
      <PageHeader title="Richieste Pro" subtitle={`${pending.length} in attesa`} />
      <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label={`In attesa (${pending.length})`} />
        <Tab label={`Risolte (${resolved.length})`} />
      </Tabs>

      {tab === 0 && (
        <Stack spacing={2}>
          {pending.length === 0 && <Typography>Nessuna richiesta in attesa.</Typography>}
          {pending.map((req) => (
            <Card key={req.id}>
              <CardContent>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  {req.imageUrl && (
                    <Box component="img" src={req.imageUrl} alt="Documento" sx={{ width: 120, height: 120, objectFit: 'cover', borderRadius: 1, cursor: 'pointer' }} onClick={() => window.open(req.imageUrl, '_blank')} />
                  )}
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="h6">{req.name}</Typography>
                    <Typography color="text.secondary">{req.email}</Typography>
                    {req.orderNumber && (
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        Ordine Amazon: <strong>{req.orderNumber}</strong>
                      </Typography>
                    )}
                    {req.message && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                        “{req.message}”
                      </Typography>
                    )}
                    <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                      <Button variant="contained" onClick={() => onApprove(req)}>Approva</Button>
                      <Button variant="outlined" color="error" onClick={() => setRejectFor(req)}>Rifiuta</Button>
                    </Stack>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}

      {tab === 1 && (
        <Stack spacing={1}>
          {resolved.length === 0 && <Typography>Nessuna richiesta risolta.</Typography>}
          {resolved.map((req) => (
            <Card key={req.id}><CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Chip size="small" color={req.resolution === 'approved' ? 'success' : 'default'} label={req.resolution || 'risolta'} />
              <Typography sx={{ flexGrow: 1 }}>{req.name} ({req.email})</Typography>
              <Typography variant="caption">{toDate(req.resolvedAt)?.toLocaleString() || ''}</Typography>
            </CardContent></Card>
          ))}
        </Stack>
      )}

      <ConfirmDialog
        open={!!rejectFor} title="Rifiuta richiesta" confirmLabel="Rifiuta" onConfirm={onReject} onClose={() => { setRejectFor(null); setReason(''); }}
        message={
          <Box>
            <Typography sx={{ mb: 2 }}>Motivo del rifiuto (opzionale):</Typography>
            <TextField fullWidth multiline rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
          </Box>
        }
      />
      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)}>
        <Alert severity={toast?.severity} onClose={() => setToast(null)}>{toast?.msg}</Alert>
      </Snackbar>
    </>
  );
}
